import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Plus, RefreshCw, Link as LinkIcon, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AFFILIATE_CODE_PATTERN,
  AgentOverrides,
  CREATOR_CODE_PATTERN,
  CreatorCode,
  OVERRIDE_AGENTS,
  buildCreatorShareLink,
  createCreatorCode,
  listCreatorCodes,
  setCreatorAgentConfig,
  updateCreatorCode,
} from '@/services/creatorCodesService';
import { useLanguage } from '@/i18n';

const PREFERRED_AGENT_NONE = '__none__';

function formatAgentLabel(agent: string): string {
  return agent.charAt(0).toUpperCase() + agent.slice(1);
}

function formatDate(value: string | null): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toISOString().slice(0, 10);
}

function countEnabledOverrides(overrides: AgentOverrides): number {
  return Object.values(overrides || {}).filter(
    (override) => override && override.enabled && (override.code ?? '').trim() !== '',
  ).length;
}

type AgentDraft = { code: string; enabled: boolean };

function AgentOverrideDialog({
  row,
  onSaved,
}: {
  row: CreatorCode;
  onSaved: (updated: CreatorCode) => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferredAgent, setPreferredAgent] = useState<string>(PREFERRED_AGENT_NONE);
  const [drafts, setDrafts] = useState<Record<string, AgentDraft>>({});

  const seed = useCallback(() => {
    setPreferredAgent(row.preferredAgent || PREFERRED_AGENT_NONE);
    const next: Record<string, AgentDraft> = {};
    for (const agent of OVERRIDE_AGENTS) {
      const existing = row.agentOverrides?.[agent];
      next[agent] = {
        code: existing?.code ?? '',
        enabled: Boolean(existing?.enabled),
      };
    }
    setDrafts(next);
  }, [row]);

  const handleOpenChange = (next: boolean) => {
    if (next) seed();
    setOpen(next);
  };

  const setDraft = (agent: string, patch: Partial<AgentDraft>) => {
    setDrafts((prev) => ({ ...prev, [agent]: { ...prev[agent], ...patch } }));
  };

  const handleSave = async () => {
    const overrides: AgentOverrides = {};
    for (const agent of OVERRIDE_AGENTS) {
      const draft = drafts[agent] || { code: '', enabled: false };
      const code = draft.code.trim();
      if (code && !AFFILIATE_CODE_PATTERN.test(code)) {
        toast.error(t('creatorAgentInvalidCode'));
        return;
      }
      if (draft.enabled && !code) {
        toast.error(t('creatorAgentEnabledNeedsCode'));
        return;
      }
      if (code || draft.enabled) {
        overrides[agent] = { code, enabled: draft.enabled };
      }
    }

    const preferred = preferredAgent === PREFERRED_AGENT_NONE ? null : preferredAgent;
    if (preferred) {
      const override = overrides[preferred];
      if (!override || !override.enabled || !override.code) {
        // Soft guidance only — saving still proceeds.
        toast.warning(t('creatorPreferredNeedsOverride'));
      }
    }

    try {
      setSaving(true);
      const updated = await setCreatorAgentConfig(row.id, preferred, overrides);
      onSaved(updated);
      toast.success(t('creatorAgentSaved'));
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('creatorUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = countEnabledOverrides(row.agentOverrides);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button type="button" size="sm" variant="outline" onClick={() => handleOpenChange(true)}>
        <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
        {t('creatorConfigureAgents')}
        {enabledCount > 0 ? (
          <Badge variant="secondary" className="ml-2">
            {enabledCount}
          </Badge>
        ) : null}
      </Button>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('creatorAgentDialogTitle', { code: row.code })}</DialogTitle>
          <DialogDescription>{t('creatorAgentDialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>{t('creatorPreferredAgentLabel')}</Label>
          <Select value={preferredAgent} onValueChange={setPreferredAgent}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PREFERRED_AGENT_NONE}>{t('creatorPreferredAgentNone')}</SelectItem>
              {OVERRIDE_AGENTS.map((agent) => (
                <SelectItem key={agent} value={agent}>
                  {formatAgentLabel(agent)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t('creatorPreferredAgentHint')}</p>
        </div>

        <div className="max-h-[45vh] overflow-y-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('creatorAgentColumn')}</TableHead>
                <TableHead>{t('creatorAgentCodeColumn')}</TableHead>
                <TableHead className="text-right">{t('creatorAgentEnabledColumn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {OVERRIDE_AGENTS.map((agent) => {
                const draft = drafts[agent] || { code: '', enabled: false };
                return (
                  <TableRow key={agent}>
                    <TableCell className="font-medium">{formatAgentLabel(agent)}</TableCell>
                    <TableCell>
                      <Input
                        value={draft.code}
                        onChange={(event) => setDraft(agent, { code: event.target.value })}
                        placeholder={t('creatorAgentCodePlaceholder')}
                        autoComplete="off"
                        spellCheck={false}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch
                        checked={draft.enabled}
                        onCheckedChange={(checked) => setDraft(agent, { enabled: checked })}
                        aria-label={`${formatAgentLabel(agent)} ${t('creatorAgentEnabledColumn')}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
            {t('creatorCancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? t('creatorSaving') : t('creatorSaveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CreatorCodes() {
  const { t } = useLanguage();
  const [codes, setCodes] = useState<CreatorCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await listCreatorCodes();
      setCodes(rows);
      setLabelDrafts(Object.fromEntries(rows.map((row) => [row.id, row.label ?? ''])));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('creatorLoadFailed');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const normalizedNewCode = newCode.trim().toLowerCase();
  const isNewCodeValid = CREATOR_CODE_PATTERN.test(normalizedNewCode);

  const handleCreate = async () => {
    if (!isNewCodeValid) {
      toast.error(t('creatorInvalidFormat'));
      return;
    }
    try {
      setCreating(true);
      const created = await createCreatorCode(normalizedNewCode, newLabel);
      setCodes((prev) => [created, ...prev]);
      setLabelDrafts((prev) => ({ ...prev, [created.id]: created.label ?? '' }));
      setNewCode('');
      setNewLabel('');
      toast.success(t('creatorCreated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('creatorUpdateFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (row: CreatorCode, enabled: boolean) => {
    try {
      setBusyId(row.id);
      const updated = await updateCreatorCode(row.id, { enabled });
      setCodes((prev) => prev.map((item) => (item.id === row.id ? updated : item)));
      toast.success(enabled ? t('creatorEnabledToast') : t('creatorDisabledToast'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('creatorUpdateFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveLabel = async (row: CreatorCode) => {
    const draft = labelDrafts[row.id] ?? '';
    if (draft === (row.label ?? '')) return;
    try {
      setBusyId(row.id);
      const updated = await updateCreatorCode(row.id, { label: draft });
      setCodes((prev) => prev.map((item) => (item.id === row.id ? updated : item)));
      toast.success(t('creatorSaved'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('creatorUpdateFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (code: string) => {
    const link = buildCreatorShareLink(code);
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('creatorLinkCopied'));
    } catch {
      toast.error(t('creatorCopyFailed'));
    }
  };

  const hasCodes = useMemo(() => codes.length > 0, [codes]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('creatorsTitle')}</h1>
        <p className="text-muted-foreground">{t('creatorsDescription')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('creatorCreateTitle')}</CardTitle>
          <CardDescription>{t('creatorCreateDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="creator-code">{t('creatorCodeLabel')}</Label>
              <Input
                id="creator-code"
                value={newCode}
                onChange={(event) => setNewCode(event.target.value)}
                placeholder={t('creatorCodePlaceholder')}
                autoComplete="off"
                spellCheck={false}
              />
              {newCode && !isNewCodeValid ? (
                <p className="text-xs text-destructive">{t('creatorInvalidFormat')}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('creatorCodeHint')}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="creator-label">{t('creatorLabelLabel')}</Label>
              <Input
                id="creator-label"
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
                placeholder={t('creatorLabelPlaceholder')}
                autoComplete="off"
              />
            </div>
            <Button type="button" onClick={handleCreate} disabled={creating || !isNewCodeValid}>
              <Plus className="mr-2 h-4 w-4" />
              {creating ? t('creatorCreating') : t('creatorCreate')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('creatorListTitle')}</CardTitle>
            <CardDescription>{t('creatorListDescription')}</CardDescription>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('creatorRefresh')}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : !hasCodes ? (
            <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
              {t('creatorEmptyDescription')}
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('creatorTableCode')}</TableHead>
                    <TableHead>{t('creatorTableLabel')}</TableHead>
                    <TableHead>{t('creatorTableStatus')}</TableHead>
                    <TableHead>{t('creatorTableAgents')}</TableHead>
                    <TableHead>{t('creatorTableCreated')}</TableHead>
                    <TableHead>{t('creatorTableUpdated')}</TableHead>
                    <TableHead className="text-right">{t('creatorTableShare')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono font-medium">{row.code}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Input
                            value={labelDrafts[row.id] ?? ''}
                            onChange={(event) =>
                              setLabelDrafts((prev) => ({ ...prev, [row.id]: event.target.value }))
                            }
                            className="h-8 w-40"
                            placeholder={t('creatorLabelPlaceholder')}
                          />
                          {(labelDrafts[row.id] ?? '') !== (row.label ?? '') && (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={busyId === row.id}
                              onClick={() => handleSaveLabel(row)}
                            >
                              {t('creatorSave')}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={row.enabled}
                            disabled={busyId === row.id}
                            onCheckedChange={(checked) => handleToggle(row, checked)}
                            aria-label={t('creatorTableStatus')}
                          />
                          <Badge variant={row.enabled ? 'default' : 'secondary'}>
                            {row.enabled ? t('creatorEnabled') : t('creatorDisabled')}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <AgentOverrideDialog
                            row={row}
                            onSaved={(updated) =>
                              setCodes((prev) =>
                                prev.map((item) => (item.id === updated.id ? updated : item)),
                              )
                            }
                          />
                          {row.preferredAgent ? (
                            <Badge variant="outline" title={t('creatorPreferredAgentLabel')}>
                              {formatAgentLabel(row.preferredAgent)}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(row.createdAt)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(row.updatedAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => copyLink(row.code)}
                          title={buildCreatorShareLink(row.code)}
                        >
                          <LinkIcon className="mr-2 h-4 w-4" />
                          <Copy className="h-3.5 w-3.5" />
                          <span className="sr-only">{t('creatorCopyLink')}</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
